import { z } from 'zod';
import {
  DBClassClassIdSchema,
  DBClassSubjectClassIdSchema,
  DBClassSubjectSchema,
} from '../classes/schemas';
import { BillingType, Location } from '../common/enums';
import {
  HumanFullNameSchema,
  LocationSchema,
  StringBooleanSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBLessonTemplateCourseSchema,
  DBLessonTemplateDurationSchema,
  DBLessonTemplateLessonIdSchema,
} from '../lessonTemplates/schemas';
import { DBRoomRoomIdSchema } from '../rooms/schemas';
import { StaffStaffIdSchema } from '../staff/schemas';
import { DBStreamStreamIdSchema } from '../streams/schemas';
import { DBStudentStudentIdSchema } from '../students/schemas';

/**
 * Shared & Helper Schemas
 */

export const DBLessonCounterSchema = z
  .string()
  .regex(/^[0-9]+$/)
  .length(4);
export const LessonCounterSchema = DBLessonCounterSchema;

/**
 * DB Schemas
 */

export const DBLessonSubjectClassIdentifierSchema = z.lazy(() => DBClassSubjectClassIdSchema);
export const DBLessonLessonSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 3 &&
    z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[0]).success &&
    z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[1]).success &&
    DBLessonCounterSchema.safeParse(parts[2]).success
  );
}, 'Must be in format course#lessonId#0000');
export const DBLessonAbsenteeCountSchema = z.number().int().min(0);
export const DBLessonBillingTypeSchema = z.enum(BillingType);
export const DBLessonCourseLessonSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[0]).success &&
    z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[1]).success
  );
}, 'Must be in format course#lessonId');
export const DBLessonDurationSchema = z.lazy(() => DBLessonTemplateDurationSchema);
export const DBLessonEnrolledStudentsSchema = z.record(
  z.lazy(() => DBStudentStudentIdSchema),
  z
    .object({
      name: HumanFullNameSchema,
    })
    .strict()
);
export const DBLessonHomeworkMarkedSchema = z.boolean();
export const DBLessonLocationSchema = LocationSchema;
export const DBLessonMakeUpCountSchema = z.number().int().min(0);
export const DBLessonNotesSchema = z.string().min(0).max(4096);
export const DBLessonOnlineCountSchema = z.number().int().min(0);
export const DBLessonRollMarkedSchema = StringBooleanSchema;
export const DBLessonRoomSchema = z.lazy(() => DBRoomRoomIdSchema);
export const DBLessonSeparatelyBilledSchema = z.boolean();
export const DBLessonStartDatetimeSchema = UnixTimestampSchema;
export const DBLessonStreamSchema = z.lazy(() => DBStreamStreamIdSchema);
export const DBLessonStudentCountSchema = z.number().int().min(0);
export const DBLessonTeacherSchema = z.lazy(() => StaffStaffIdSchema).or(z.literal(''));

export const DBLessonSchema = z.object({
  subjectClassIdentifier: DBLessonSubjectClassIdentifierSchema,
  lesson: DBLessonLessonSchema,
  absenteeCount: DBLessonAbsenteeCountSchema,
  billingType: DBLessonBillingTypeSchema,
  courseLesson: DBLessonCourseLessonSchema,
  duration: DBLessonDurationSchema,
  // Optional to allow removal of a specific student
  enrolledStudents: DBLessonEnrolledStudentsSchema.optional(),
  homeworkMarked: DBLessonHomeworkMarkedSchema,
  location: DBLessonLocationSchema,
  makeUpCount: DBLessonMakeUpCountSchema,
  notes: DBLessonNotesSchema,
  onlineCount: DBLessonOnlineCountSchema,
  rollMarked: DBLessonRollMarkedSchema,
  room: DBLessonRoomSchema,
  separatelyBilled: DBLessonSeparatelyBilledSchema,
  startDatetime: DBLessonStartDatetimeSchema,
  stream: DBLessonStreamSchema,
  studentCount: DBLessonStudentCountSchema,
  teacher: DBLessonTeacherSchema,
});

/**
 * Client Schemas
 */

export const LessonClassIdSchema = z.object({
  subject: z.lazy(() => DBClassSubjectSchema),
  classId: z.lazy(() => DBClassClassIdSchema),
});
export const LessonCourseSchema = z.lazy(() => DBLessonTemplateCourseSchema);
export const LessonLessonSchema = z.object({
  course: z.lazy(() => DBLessonTemplateCourseSchema),
  lessonId: z.lazy(() => DBLessonTemplateLessonIdSchema),
  counter: LessonCounterSchema,
});
export const LessonAbsenteeCountSchema = DBLessonAbsenteeCountSchema;
export const LessonBillingTypeSchema = DBLessonBillingTypeSchema;
export const LessonDurationSchema = DBLessonDurationSchema;
export const LessonEnrolledStudentsSchema = z.array(
  z
    .object({
      studentId: z.lazy(() => DBStudentStudentIdSchema),
      name: HumanFullNameSchema,
    })
    .strict()
);
export const LessonHomeworkMarkedSchema = DBLessonHomeworkMarkedSchema;
export const LessonLocationSchema = DBLessonLocationSchema;
export const LessonMakeUpCountSchema = DBLessonMakeUpCountSchema;
export const LessonNotesSchema = DBLessonNotesSchema;
export const LessonOnlineCountSchema = DBLessonOnlineCountSchema;
export const LessonRollMarkedSchema = z.boolean();
export const LessonRoomSchema = DBLessonRoomSchema;
export const LessonSeparatelyBilledSchema = DBLessonSeparatelyBilledSchema;
export const LessonStartDatetimeSchema = DBLessonStartDatetimeSchema;
export const LessonStreamSchema = DBLessonStreamSchema;
export const LessonStudentCountSchema = DBLessonStudentCountSchema;
export const LessonTeacherSchema = DBLessonTeacherSchema;

export const LessonSchema = z.object({
  classId: LessonClassIdSchema,
  course: LessonCourseSchema,
  lesson: LessonLessonSchema,
  absenteeCount: LessonAbsenteeCountSchema.default(0),
  billingType: LessonBillingTypeSchema.default(BillingType.Term),
  duration: LessonDurationSchema,
  enrolledStudents: LessonEnrolledStudentsSchema,
  homeworkMarked: LessonHomeworkMarkedSchema.default(false),
  location: LessonLocationSchema.default(Location.Chatswood),
  makeUpCount: LessonMakeUpCountSchema.default(0),
  notes: LessonNotesSchema.default(''),
  onlineCount: LessonOnlineCountSchema.default(0),
  rollMarked: LessonRollMarkedSchema.default(false),
  room: LessonRoomSchema,
  separatelyBilled: LessonSeparatelyBilledSchema.default(false),
  startDatetime: LessonStartDatetimeSchema,
  stream: LessonStreamSchema,
  studentCount: LessonStudentCountSchema.default(0),
  teacher: LessonTeacherSchema,
});
